import type { Point, UtilityElement, UtilityType, Wall, FloorPlanElement, Room } from '../types';
import { distanceToLine, isPointInsideRoom } from './floorPlanUtils';
import { SNAP_THRESHOLD, WALL_THICKNESS, PIXELS_PER_METER } from '../constants/floorPlanConstants';

const UTILITY_CONFIG: Record<UtilityType, { text: string; color: string; bgColor?: string; isDrawn?: boolean; }> = {
    'gas-meter': { text: 'GM', color: '#000000', bgColor: '#bbf7d0' }, // Black text, light green background
    'electric-meter': { text: 'EM', color: '#000000', bgColor: '#bbf7d0' }, // Black text, light green background
    'stop-tap': { text: 'S-TAP', color: '#000000', bgColor: '#bbf7d0' }, // Black text, light green background
    'tank-cylinder': { text: 'TNK', color: '#000000', bgColor: '#bbf7d0' }, // Black text, light green background
    'consumer-unit': { text: 'CU', color: '#000000', bgColor: '#bbf7d0' }, // Black text, light green background
    'fan': { text: 'F', color: '#000000', bgColor: '#bbf7d0' }, // Black text, light green background
    'background-vent': { text: 'V', color: '#000000', bgColor: '#bbf7d0' }, // Black text, light green background
    'thermostat': { text: 'T-Stat', color: '#000000', bgColor: '#bbf7d0' }, // Black text, light green background
    'air-source-hp': { text: 'ASHP', color: '#000000', bgColor: '#bbf7d0' }, // Black text, light green background
    'garden': { text: 'GARDEN', color: '#000000', bgColor: '#d1fae5', isDrawn: true }, // Black text, light green background, custom drawn
    'single-sink': { text: 'SINK', color: '#000000', bgColor: '#93c5fd', isDrawn: true }, // Black text, light blue background, custom drawn
    'double-sink': { text: 'D-SINK', color: '#000000', bgColor: '#93c5fd', isDrawn: true }, // Black text, light blue background, custom drawn
    'hob': { text: 'HOB', color: '#000000', bgColor: '#ffffff', isDrawn: true }, // 🏢 Custom drawn for estate plans
    'bathtub': { text: 'BATH', color: '#000000', bgColor: '#ffffff', isDrawn: true }, // 🏢 Custom drawn for estate plans
    'shower': { text: 'SH', color: '#000000', bgColor: '#ffffff', isDrawn: true }, // 🏢 Custom drawn for estate plans
    'toilet': { text: 'WC', color: '#000000', bgColor: '#ffffff', isDrawn: true }, // 🏢 Custom drawn for estate plans
};

const UTILITY_SIZE = 40;
const DEFAULT_SINK_WIDTH = 60;
const DEFAULT_SINK_DEPTH = 10; // Depth from wall
const SINK_WALL_OFFSET = 20; // Offset from wall surface for visibility

function getClosestPointOnSegment(point: Point, start: Point, end: Point): Point {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) return start;
  const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy);
  const clampedT = Math.max(0, Math.min(1, t));
  return { x: start.x + clampedT * dx, y: start.y + clampedT * dy };
}

/**
 * Find the nearest wall to a point (for sink/bath/shower/toilet placement)
 * Uses larger snap threshold for easier wall attachment
 */
export function findWallForSink(point: Point, walls: (Wall & { tempId: string, stableId: string })[]): (Wall & { tempId: string, stableId: string }) | null {
  let clickedWall: (Wall & { tempId: string, stableId: string }) | null = null;
  let minDistance = Infinity;
  
  // Use larger snap threshold (50px = 50cm) for bathroom fixtures to make wall attachment easier
  // This allows users to click anywhere near a wall instead of precisely on it
  const BATHROOM_FIXTURE_SNAP_THRESHOLD = 50;

  for (const wall of walls) {
    const distance = distanceToLine(point, wall.start, wall.end);
    if (distance < BATHROOM_FIXTURE_SNAP_THRESHOLD) {
      if (distance < minDistance) {
        minDistance = distance;
        clickedWall = wall;
      }
    }
  }
  return clickedWall;
}

/**
 * Calculate rotation for sink to face the wall (tap toward wall)
 * Unlike radiators which are parallel to walls, sinks should face perpendicular
 */
function calculateSinkRotationToFaceWall(wall: Wall, point: Point): number {
  // Calculate wall angle
  const wallDx = wall.end.x - wall.start.x;
  const wallDy = wall.end.y - wall.start.y;
  const wallAngle = Math.atan2(wallDy, wallDx);
  
  // Calculate perpendicular angle (sink faces the wall, not along it)
  const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
  
  // Determine which side of the wall the click is on
  const crossProduct = wallDx * (point.y - wall.start.y) - wallDy * (point.x - wall.start.x);
  const side = crossProduct > 0 ? -1 : 1;
  
  // Sink rotation: perpendicular to wall, facing into room
  // Tap is at top of sink (negative Y) when rotation=0
  // Calculate angle of offset direction (into room)
  const offsetAngle = Math.atan2(perpVector.y * side, perpVector.x * side);
  // Rotate sink so tap points in offset direction
  const sinkRotation = offsetAngle - Math.PI / 2;
  
  return sinkRotation;
}

/**
 * Create a sink element constrained to a specific wall (like radiators)
 */
export function createSinkElementOnWall(
  point: Point,
  wall: Wall & { tempId: string, stableId: string },
  utilityType: 'single-sink' | 'double-sink',
  floor: number
): UtilityElement {
  const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  
  // Snap point to wall (like radiators and boilers)
  const closestPointOnWall = getClosestPointOnSegment(point, wall.start, wall.end);
  const distFromStart = Math.hypot(closestPointOnWall.x - wall.start.x, closestPointOnWall.y - wall.start.y);
  
  // Calculate position along wall (normalized 0-1)
  let positionOnWall = distFromStart / wallLength;
  
  // Keep sink within wall bounds (like radiators)
  const halfSinkWidthOnWall = (DEFAULT_SINK_WIDTH / 2) / wallLength;
  positionOnWall = Math.max(halfSinkWidthOnWall, Math.min(positionOnWall, 1 - halfSinkWidthOnWall));
  
  // Calculate sink center on wall
  const sinkCenterOnWall = {
    x: wall.start.x + (wall.end.x - wall.start.x) * positionOnWall,
    y: wall.start.y + (wall.end.y - wall.start.y) * positionOnWall,
  };
  
  // Calculate perpendicular offset from wall
  const wallDx = wall.end.x - wall.start.x;
  const wallDy = wall.end.y - wall.start.y;
  const wallAngle = Math.atan2(wallDy, wallDx);
  const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
  
  // Determine which side of the wall the click is on
  const crossProduct = wallDx * (point.y - wall.start.y) - wallDy * (point.x - wall.start.x);
  const side = crossProduct > 0 ? -1 : 1;
  
  // Offset sink from wall (like radiators)
  const totalOffset = (WALL_THICKNESS / 2 + SINK_WALL_OFFSET + DEFAULT_SINK_DEPTH / 2) * side;
  
  const sinkCenter = {
    x: sinkCenterOnWall.x + perpVector.x * totalOffset,
    y: sinkCenterOnWall.y + perpVector.y * totalOffset,
  };
  
  // Calculate rotation to face away from wall (into room)
  // Tap is at top of sink (negative Y) when rotation=0
  // Calculate angle of offset direction (away from wall based on click side)
  const offsetAngle = Math.atan2(perpVector.y * side, perpVector.x * side);
  // Rotate sink so tap points in offset direction
  const rotation = offsetAngle - Math.PI / 2;
  
  return {
    id: `utility-preview-${Date.now()}`,
    type: 'utility',
    utilityType,
    position: sinkCenter,
    floor,
    rotation,
  };
}

/**
 * Create bathtub/shower/toilet element constrained to a specific wall (like sinks and counters)
 */
export function createBathroomFixtureOnWall(
  point: Point,
  wall: Wall & { tempId: string, stableId: string },
  utilityType: 'bathtub' | 'shower' | 'toilet',
  floor: number
): UtilityElement {
  const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  
  // Snap point to wall
  const closestPointOnWall = getClosestPointOnSegment(point, wall.start, wall.end);
  const distFromStart = Math.hypot(closestPointOnWall.x - wall.start.x, closestPointOnWall.y - wall.start.y);
  
  // Calculate position along wall (normalized 0-1)
  let positionOnWall = distFromStart / wallLength;
  
  // Define fixture dimensions (used for keeping within wall bounds)
  const fixtureWidth = utilityType === 'bathtub' ? 180 : (utilityType === 'shower' ? 90 : 60);
  const halfFixtureWidthOnWall = (fixtureWidth / 2) / wallLength;
  positionOnWall = Math.max(halfFixtureWidthOnWall, Math.min(positionOnWall, 1 - halfFixtureWidthOnWall));
  
  // Calculate fixture center on wall
  const fixtureCenterOnWall = {
    x: wall.start.x + (wall.end.x - wall.start.x) * positionOnWall,
    y: wall.start.y + (wall.end.y - wall.start.y) * positionOnWall,
  };
  
  // Calculate perpendicular offset from wall
  const wallDx = wall.end.x - wall.start.x;
  const wallDy = wall.end.y - wall.start.y;
  const wallAngle = Math.atan2(wallDy, wallDx);
  const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
  
  // Determine which side of the wall the click is on
  const crossProduct = wallDx * (point.y - wall.start.y) - wallDy * (point.x - wall.start.x);
  const side = crossProduct > 0 ? -1 : 1;
  
  // Calculate depth based on fixture type
  const fixtureDepth = utilityType === 'bathtub' ? 70 : (utilityType === 'shower' ? 90 : 60);
  
  // Offset fixture from wall (against wall, then offset into room by fixture depth)
  const totalOffset = (WALL_THICKNESS / 2 + fixtureDepth / 2) * side;
  
  const fixtureCenter = {
    x: fixtureCenterOnWall.x + perpVector.x * totalOffset,
    y: fixtureCenterOnWall.y + perpVector.y * totalOffset,
  };
  
  // Calculate rotation to face away from wall (into room)
  const offsetAngle = Math.atan2(perpVector.y * side, perpVector.x * side);
  const rotation = offsetAngle - Math.PI / 2;
  
  return {
    id: `utility-preview-${Date.now()}`,
    type: 'utility',
    utilityType,
    position: fixtureCenter,
    floor,
    rotation,
  };
}

export function createUtilityElement(
  point: Point,
  utilityType: UtilityType,
  floor: number,
  rotation: number = 0,
  walls?: (Wall & { tempId: string, stableId: string })[]
): UtilityElement {
  let finalRotation = rotation;
  
  // For sinks, bathtubs, and showers - automatically detect nearest wall and orient toward it
  const needsWallSnapping = ['single-sink', 'double-sink', 'bathtub', 'shower'].includes(utilityType);
  if (needsWallSnapping && walls && walls.length > 0) {
    const nearestWall = findWallForSink(point, walls);
    if (nearestWall) {
      finalRotation = calculateSinkRotationToFaceWall(nearestWall, point);
    }
  }
  
  const baseElement: UtilityElement = {
    id: `utility-preview-${Date.now()}`,
    type: 'utility' as const,
    utilityType,
    position: point,
    floor,
    rotation: finalRotation,
  };
  
  // Initialize garden with proper dimensions
  if (utilityType === 'garden') {
    return {
      ...baseElement,
      width: 300,
      height: 200,
      area: (300 * 200) / (100 * 100), // Convert pixels to m²
      snappedWalls: []
    } as UtilityElement;
  }
  
  return baseElement;
}

function drawSink(
    ctx: CanvasRenderingContext2D,
    center: Point,
    size: number,
    bgColor: string,
    isDouble: boolean = false
) {
    const sinkWidth = isDouble ? size * 2.5 : size * 1.5;
    const sinkHeight = size * 1.2;
    const basinWidth = isDouble ? size * 0.9 : size * 1.1;
    const basinHeight = size * 0.8;
    const faucetSize = size * 0.3;

    // Draw counter/base
    ctx.fillStyle = bgColor;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.fillRect(-sinkWidth / 2, -sinkHeight / 2, sinkWidth, sinkHeight);
    ctx.strokeRect(-sinkWidth / 2, -sinkHeight / 2, sinkWidth, sinkHeight);

    // Draw sink basin(s)
    ctx.fillStyle = '#ffffff';
    if (isDouble) {
        // Left basin
        ctx.fillRect(-sinkWidth / 2 + size * 0.3, -basinHeight / 2, basinWidth, basinHeight);
        ctx.strokeRect(-sinkWidth / 2 + size * 0.3, -basinHeight / 2, basinWidth, basinHeight);
        
        // Right basin
        ctx.fillRect(sinkWidth / 2 - size * 0.3 - basinWidth, -basinHeight / 2, basinWidth, basinHeight);
        ctx.strokeRect(sinkWidth / 2 - size * 0.3 - basinWidth, -basinHeight / 2, basinWidth, basinHeight);
    } else {
        // Single basin (centered)
        ctx.fillRect(-basinWidth / 2, -basinHeight / 2, basinWidth, basinHeight);
        ctx.strokeRect(-basinWidth / 2, -basinHeight / 2, basinWidth, basinHeight);
    }

    // Draw faucet (top center)
    ctx.fillStyle = '#6b7280'; // Grey color for faucet
    ctx.beginPath();
    ctx.arc(0, -sinkHeight / 2 + faucetSize / 2, faucetSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
}

// 🏢 ESTATE MODE: Hob drawing function
function drawHob(
    ctx: CanvasRenderingContext2D,
    center: Point,
    size: number,
    bgColor: string,
    isEstate: boolean = false
) {
    const hobWidth = size * 2.0;
    const hobHeight = size * 1.5;
    const burnerSize = size * 0.35;
    
    // Draw hob base
    ctx.fillStyle = isEstate ? '#ffffff' : bgColor;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.fillRect(-hobWidth / 2, -hobHeight / 2, hobWidth, hobHeight);
    ctx.strokeRect(-hobWidth / 2, -hobHeight / 2, hobWidth, hobHeight);
    
    // Draw 4 burners in a 2x2 grid
    const spacing = size * 0.6;
    const positions = [
        { x: -spacing / 2, y: -spacing / 2 }, // Top-left
        { x: spacing / 2, y: -spacing / 2 },  // Top-right
        { x: -spacing / 2, y: spacing / 2 },  // Bottom-left
        { x: spacing / 2, y: spacing / 2 }    // Bottom-right
    ];
    
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1.5;
    positions.forEach(pos => {
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, burnerSize, 0, Math.PI * 2);
        ctx.stroke();
        // Inner circle for burner detail
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, burnerSize * 0.5, 0, Math.PI * 2);
        ctx.stroke();
    });
}

// 🏢 ESTATE MODE: Bathtub drawing function
function drawBathtub(
    ctx: CanvasRenderingContext2D,
    center: Point,
    size: number,
    bgColor: string,
    isEstate: boolean = false
) {
    const bathWidth = size * 3.75;
    const bathHeight = size * 1.3;
    
    // Draw bathtub outline
    ctx.fillStyle = isEstate ? '#ffffff' : bgColor;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.fillRect(-bathWidth / 2, -bathHeight / 2, bathWidth, bathHeight);
    ctx.strokeRect(-bathWidth / 2, -bathHeight / 2, bathWidth, bathHeight);
    
    // Draw tap at one end
    const tapSize = size * 0.25;
    ctx.fillStyle = '#6b7280';
    ctx.beginPath();
    ctx.arc(-bathWidth / 2 + size * 0.4, 0, tapSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
}

// 🏢 ESTATE MODE: Shower drawing function
function drawShower(
    ctx: CanvasRenderingContext2D,
    center: Point,
    size: number,
    bgColor: string,
    isEstate: boolean = false
) {
    const showerSize = size * 1.5;
    
    // Draw shower tray (square)
    ctx.fillStyle = isEstate ? '#ffffff' : bgColor;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.fillRect(-showerSize / 2, -showerSize / 2, showerSize, showerSize);
    ctx.strokeRect(-showerSize / 2, -showerSize / 2, showerSize, showerSize);
    
    // Draw shower head (circle at top)
    const headSize = size * 0.3;
    ctx.fillStyle = '#6b7280';
    ctx.beginPath();
    ctx.arc(0, -showerSize / 2 + headSize, headSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    
    // Draw water spray lines
    ctx.strokeStyle = '#9ca3af';
    ctx.lineWidth = 1;
    for (let i = -2; i <= 2; i++) {
        ctx.beginPath();
        ctx.moveTo(i * size * 0.15, -showerSize / 2 + headSize * 2);
        ctx.lineTo(i * size * 0.2, showerSize / 2 - size * 0.2);
        ctx.stroke();
    }
}

// 🏢 ESTATE MODE: Toilet drawing function
function drawToilet(
    ctx: CanvasRenderingContext2D,
    center: Point,
    size: number,
    bgColor: string,
    isEstate: boolean = false
) {
    const toiletWidth = size * 1.0;
    const toiletHeight = size * 2.4;
    const bowlWidth = size * 1.0;
    const bowlHeight = size * 1.3;
    const tankWidth = size * 1.35;
    const tankHeight = size * 0.9;
    
    // Draw toilet bowl (rounded rectangle)
    ctx.fillStyle = isEstate ? '#ffffff' : bgColor;
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(0, size * 0.1, bowlWidth / 2, bowlHeight / 2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    
    // Draw tank
    ctx.fillRect(-tankWidth / 2, -toiletHeight / 2, tankWidth, tankHeight);
    ctx.strokeRect(-tankWidth / 2, -toiletHeight / 2, tankWidth, tankHeight);
}

// Garden drawing moved to exported drawGarden function below

function drawUtilityText(
    ctx: CanvasRenderingContext2D,
    text: string,
    center: Point,
    size: number,
    bgColor: string = '#f3f4f6',
    rounded: boolean = false
) {
    const originalFont = ctx.font;
    ctx.font = `bold ${size}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Always draw background box for utility elements
    // Increase padding to fully cover longer labels (e.g., T-Stat)
    const paddingX = Math.max(2, size * 0.4);
    const paddingY = Math.max(1, size * 0.3);
    const textWidth = ctx.measureText(text).width || (text.length * size * 0.6);
    const boxWidth = textWidth + paddingX * 2;
    const boxHeight = size + paddingY * 2;
    
    // Draw background
    ctx.fillStyle = bgColor;
    if (rounded) {
        const corner = Math.min(boxHeight / 2, boxWidth / 2);
        ctx.beginPath();
        ctx.moveTo(center.x - boxWidth/2 + corner, center.y - boxHeight/2);
        ctx.lineTo(center.x + boxWidth/2 - corner, center.y - boxHeight/2);
        ctx.quadraticCurveTo(center.x + boxWidth/2, center.y - boxHeight/2, center.x + boxWidth/2, center.y - boxHeight/2 + corner);
        ctx.lineTo(center.x + boxWidth/2, center.y + boxHeight/2 - corner);
        ctx.quadraticCurveTo(center.x + boxWidth/2, center.y + boxHeight/2, center.x + boxWidth/2 - corner, center.y + boxHeight/2);
        ctx.lineTo(center.x - boxWidth/2 + corner, center.y + boxHeight/2);
        ctx.quadraticCurveTo(center.x - boxWidth/2, center.y + boxHeight/2, center.x - boxWidth/2, center.y + boxHeight/2 - corner);
        ctx.lineTo(center.x - boxWidth/2, center.y - boxHeight/2 + corner);
        ctx.quadraticCurveTo(center.x - boxWidth/2, center.y - boxHeight/2, center.x - boxWidth/2 + corner, center.y - boxHeight/2);
        ctx.closePath();
        ctx.fill();

        // Draw black border
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        ctx.stroke();
    } else {
        ctx.fillRect(center.x - boxWidth/2, center.y - boxHeight/2, boxWidth, boxHeight);
        
        // Draw black border
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        ctx.strokeRect(center.x - boxWidth/2, center.y - boxHeight/2, boxWidth, boxHeight);
    }
    
    // Reset fill style to black for text (since all utility text should be black)
    ctx.fillStyle = '#000000';
    // Draw text
    ctx.fillText(text, center.x, center.y);
    ctx.font = originalFont;
}

export function drawUtilities(
  ctx: CanvasRenderingContext2D,
  elements: FloorPlanElement[],
  currentFloor: number,
  scale: number,
  viewOffset: Point,
  isEstate: boolean = false
) {
  const utilityElements = elements.filter(
    (el): el is UtilityElement => el.type === 'utility' && el.floor === currentFloor
  );

  if (utilityElements.length === 0) return;

  ctx.save();
  // Canvas context is already transformed, no need to apply transformation again

  utilityElements.forEach(util => {
    const config = UTILITY_CONFIG[util.utilityType];
    if (config) {
      // 🔧 CANVAS SIZE: 50% smaller for specific utilities in canvas (PDF export uses normal size)
      const needsSmallerCanvas = ['gas-meter', 'electric-meter', 'stop-tap', 'tank-cylinder', 'thermostat', 'consumer-unit', 'air-source-hp', 'fan', 'background-vent'].includes(util.utilityType);
      const baseSize = 0.27 * PIXELS_PER_METER; // Base size in pixels
      const canvasSizeMultiplier = needsSmallerCanvas ? 0.5 : 1.0; // 50% smaller for specific utilities
      const size = (baseSize * canvasSizeMultiplier) / scale;
      // 🏢 ESTATE MODE: Use white for all backgrounds
      const bgColor = isEstate ? '#ffffff' : (config.bgColor || '#f3f4f6');
      
      // Handle custom drawn elements with rotation
      if (config.isDrawn) {
        ctx.save();
        ctx.translate(util.position.x, util.position.y);
        ctx.rotate(util.rotation || 0);
        
        if (util.utilityType === 'single-sink' || util.utilityType === 'double-sink') {
          drawSink(ctx, { x: 0, y: 0 }, size, bgColor, util.utilityType === 'double-sink');
        } else if (util.utilityType === 'hob') {
          drawHob(ctx, { x: 0, y: 0 }, size, bgColor, isEstate);
        } else if (util.utilityType === 'bathtub') {
          drawBathtub(ctx, { x: 0, y: 0 }, size, bgColor, isEstate);
        } else if (util.utilityType === 'shower') {
          drawShower(ctx, { x: 0, y: 0 }, size, bgColor, isEstate);
        } else if (util.utilityType === 'toilet') {
          drawToilet(ctx, { x: 0, y: 0 }, size, bgColor, isEstate);
        } else if (util.utilityType === 'garden') {
          drawGarden(ctx, util, scale, false, []); // No handles, no room cutout in utility batch
        }
        
        ctx.restore();
      } else {
        // Regular text-based utilities
        const needsRounded = util.utilityType === 'tank-cylinder' || util.utilityType === 'thermostat';
        drawUtilityText(ctx, config.text, util.position, size, config.bgColor || '#f3f4f6', needsRounded);
      }
    }
  });
  
  ctx.restore();
}

/**
 * Check if a point is inside a garden element
 */
export function isPointInsideGarden(point: Point, garden: UtilityElement): boolean {
  if (garden.utilityType !== 'garden' || !garden.width || !garden.height) return false;
  
  const halfWidth = garden.width / 2;
  const halfHeight = garden.height / 2;
  
  return (
    point.x >= garden.position.x - halfWidth &&
    point.x <= garden.position.x + halfWidth &&
    point.y >= garden.position.y - halfHeight &&
    point.y <= garden.position.y + halfHeight
  );
}

/**
 * Get the resize handle at a point for a garden element
 * Returns corner identifier: 'top-left', 'top-right', 'bottom-left', 'bottom-right', or null
 */
export function getGardenResizeHandle(
  point: Point,
  garden: UtilityElement,
  threshold: number = 20
): 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null {
  if (garden.utilityType !== 'garden' || !garden.width || !garden.height) return null;
  
  const halfWidth = garden.width / 2;
  const halfHeight = garden.height / 2;
  
  const corners = {
    'top-left': { x: garden.position.x - halfWidth, y: garden.position.y - halfHeight },
    'top-right': { x: garden.position.x + halfWidth, y: garden.position.y - halfHeight },
    'bottom-left': { x: garden.position.x - halfWidth, y: garden.position.y + halfHeight },
    'bottom-right': { x: garden.position.x + halfWidth, y: garden.position.y + halfHeight }
  };
  
  for (const [handle, corner] of Object.entries(corners)) {
    const dist = Math.hypot(point.x - corner.x, point.y - corner.y);
    if (dist <= threshold) {
      return handle as 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    }
  }
  
  return null;
}

/**
 * Check if a garden overlaps with any rooms or elements
 * Returns true if collision detected
 */
export function checkGardenCollision(
  garden: UtilityElement,
  rooms: Room[],
  elements: FloorPlanElement[]
): boolean {
  // For polygon gardens, use point-in-polygon tests
  if (garden.points && garden.points.length >= 3) {
    // Import isPointInsidePolygon from gardenSnapping
    const { isPointInsidePolygon } = require('./gardenSnapping');
    
    // Check if any room overlaps with garden polygon
    for (const room of rooms) {
      // Check if any room corner is inside garden
      const roomPoints = room.walls.flatMap((w: Wall) => [w.start, w.end]);
      for (const point of roomPoints) {
        if (isPointInsidePolygon(point, garden.points)) {
          return true;
        }
      }
      
      // Check if any garden point is inside room (room bounds check)
      const roomBounds = {
        minX: Math.min(...roomPoints.map((p: Point) => p.x)),
        maxX: Math.max(...roomPoints.map((p: Point) => p.x)),
        minY: Math.min(...roomPoints.map((p: Point) => p.y)),
        maxY: Math.max(...roomPoints.map((p: Point) => p.y))
      };
      
      for (const point of garden.points) {
        if (point.x >= roomBounds.minX && point.x <= roomBounds.maxX &&
            point.y >= roomBounds.minY && point.y <= roomBounds.maxY) {
          return true;
        }
      }
    }
    
    // Check overlap with other elements
    for (const el of elements) {
      if ('id' in el && (el as any).id === garden.id) continue;
      if (el.type === 'utility' && (el as UtilityElement).utilityType === 'garden') {
        // Check garden-to-garden overlap
        const otherGarden = el as UtilityElement;
        if (otherGarden.points && otherGarden.points.length >= 3) {
          // Check if any point of one garden is inside the other
          for (const point of garden.points) {
            if (isPointInsidePolygon(point, otherGarden.points)) {
              return true;
            }
          }
        }
      }
    }
    
    return false;
  }
  
  // Legacy rectangular garden collision detection
  if (!garden.width || !garden.height) return false;
  
  const gardenBounds = {
    minX: garden.position.x - garden.width / 2,
    maxX: garden.position.x + garden.width / 2,
    minY: garden.position.y - garden.height / 2,
    maxY: garden.position.y + garden.height / 2
  };
  
  // Check collision with rooms - calculate bounds from walls
  for (const room of rooms) {
    // Calculate room bounds from walls
    const roomPoints = room.walls.flatMap((w: Wall) => [w.start, w.end]);
    if (roomPoints.length === 0) continue;
    
    const roomBounds = {
      minX: Math.min(...roomPoints.map((p: Point) => p.x)),
      maxX: Math.max(...roomPoints.map((p: Point) => p.x)),
      minY: Math.min(...roomPoints.map((p: Point) => p.y)),
      maxY: Math.max(...roomPoints.map((p: Point) => p.y))
    };
    
    const overlaps = !(
      gardenBounds.maxX < roomBounds.minX ||
      gardenBounds.minX > roomBounds.maxX ||
      gardenBounds.maxY < roomBounds.minY ||
      gardenBounds.minY > roomBounds.maxY
    );
    
    if (overlaps) {
      return true; // Collision detected with room
    }
  }
  
  // Check collision with other elements (excluding gardens and self)
  for (const element of elements) {
    // Skip self and other gardens
    if (element.type === 'utility' && (element as UtilityElement).utilityType === 'garden') {
      // Allow overlap with other gardens
      continue;
    }
    
    // Skip elements without position
    if (!('position' in element)) continue;
    
    // Simple point-in-bounds check for other elements
    const elementPos = (element as any).position as Point;
    const inGarden = 
      elementPos.x >= gardenBounds.minX &&
      elementPos.x <= gardenBounds.maxX &&
      elementPos.y >= gardenBounds.minY &&
      elementPos.y <= gardenBounds.maxY;
    
    if (inGarden) {
      return true; // Collision detected with element
    }
  }
  
  return false; // No collision
}

/**
 * Resize a garden element by dragging a corner
 */
export function resizeGarden(
  garden: UtilityElement,
  handle: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right',
  newPoint: Point
): UtilityElement {
  if (garden.utilityType !== 'garden' || !garden.width || !garden.height) return garden;
  
  const halfWidth = garden.width / 2;
  const halfHeight = garden.height / 2;
  
  // Calculate current corners
  const topLeft = { x: garden.position.x - halfWidth, y: garden.position.y - halfHeight };
  const topRight = { x: garden.position.x + halfWidth, y: garden.position.y - halfHeight };
  const bottomLeft = { x: garden.position.x - halfWidth, y: garden.position.y + halfHeight };
  const bottomRight = { x: garden.position.x + halfWidth, y: garden.position.y + halfHeight };
  
  // Update the dragged corner
  let newTopLeft = topLeft;
  let newBottomRight = bottomRight;
  
  switch (handle) {
    case 'top-left':
      newTopLeft = newPoint;
      newBottomRight = bottomRight;
      break;
    case 'top-right':
      newTopLeft = { x: topLeft.x, y: newPoint.y };
      newBottomRight = { x: newPoint.x, y: bottomRight.y };
      break;
    case 'bottom-left':
      newTopLeft = { x: newPoint.x, y: topLeft.y };
      newBottomRight = { x: bottomRight.x, y: newPoint.y };
      break;
    case 'bottom-right':
      newTopLeft = topLeft;
      newBottomRight = newPoint;
      break;
  }
  
  // Calculate new dimensions and center
  const newWidth = Math.abs(newBottomRight.x - newTopLeft.x);
  const newHeight = Math.abs(newBottomRight.y - newTopLeft.y);
  
  // Minimum size constraints
  const minSize = 50;
  if (newWidth < minSize || newHeight < minSize) return garden;
  
  const newCenter = {
    x: (newTopLeft.x + newBottomRight.x) / 2,
    y: (newTopLeft.y + newBottomRight.y) / 2
  };
  
  // Calculate area in m²
  const PIXELS_PER_METER = 100;
  const area = (newWidth * newHeight) / (PIXELS_PER_METER * PIXELS_PER_METER);
  
  return {
    ...garden,
    position: newCenter,
    width: newWidth,
    height: newHeight,
    area
  };
}

/**
 * Draw a single garden element with optional resize handles
 * Called by canvasRenderer for separated garden rendering
 */
export function drawGarden(
  ctx: CanvasRenderingContext2D,
  element: UtilityElement,
  scale: number,
  showHandles: boolean = false,
  rooms: Room[] = [],
  isEstate: boolean = false
): void {
  const size = 27;
  const center = element.position;
  
  ctx.save();
  
  // Draw polygon-based garden if points exist
  if (element.points && element.points.length >= 3) {
    // Create clipping region - exclude areas that overlap with rooms
    ctx.save();
    
    // Build the clipping path (garden minus rooms)
    ctx.beginPath();
    ctx.moveTo(element.points[0].x, element.points[0].y);
    for (let i = 1; i < element.points.length; i++) {
      ctx.lineTo(element.points[i].x, element.points[i].y);
    }
    ctx.closePath();
    
    // If rooms exist, subtract them from the garden
    if (rooms && rooms.length > 0) {
      for (const room of rooms) {
        if (room.walls && room.walls.length >= 3) {
          ctx.moveTo(room.walls[0].start.x, room.walls[0].start.y);
          for (const wall of room.walls) {
            ctx.lineTo(wall.end.x, wall.end.y);
          }
          ctx.closePath();
        }
      }
    }
    
    // Apply clipping region with even-odd rule
    ctx.clip('evenodd');
    
    // Now fill a large rectangle within the clip - only visible parts will show
    // 🏢 ESTATE MODE: Use light gray for B&W rendering
    ctx.fillStyle = isEstate ? 'rgba(0, 0, 0, 0.08)' : 'rgba(34, 197, 94, 0.15)';
    const bounds = {
      minX: Math.min(...element.points.map(p => p.x)),
      maxX: Math.max(...element.points.map(p => p.x)),
      minY: Math.min(...element.points.map(p => p.y)),
      maxY: Math.max(...element.points.map(p => p.y))
    };
    ctx.fillRect(bounds.minX - 10, bounds.minY - 10, bounds.maxX - bounds.minX + 20, bounds.maxY - bounds.minY + 20);
    
    // Restore to remove clip before drawing border
    ctx.restore();
    
    // Save again for border drawing
    ctx.save();
    
    // Draw dotted border only for garden boundary
    ctx.beginPath();
    ctx.moveTo(element.points[0].x, element.points[0].y);
    for (let i = 1; i < element.points.length; i++) {
      ctx.lineTo(element.points[i].x, element.points[i].y);
    }
    ctx.closePath();
    // 🏢 ESTATE MODE: Use black for B&W rendering
    ctx.strokeStyle = isEstate ? '#000000' : '#22c55e';
    ctx.lineWidth = 2 / scale;
    ctx.setLineDash([10 / scale, 5 / scale]);
    ctx.stroke();
    ctx.setLineDash([]);
    
    // CRITICAL: Reset canvas state before restore to prevent leaking
    ctx.fillStyle = 'transparent';
    ctx.strokeStyle = '#000000';
    ctx.globalAlpha = 1.0;
    
    ctx.restore();
    
    // Draw label at center
    ctx.translate(center.x, center.y);
  } else {
    // Legacy rectangular garden
    const gardenWidth = element.width || (size * 8);
    const gardenHeight = element.height || (size * 6);
    
    ctx.translate(center.x, center.y);
    
    // Create clipping region for rectangular garden
    ctx.save();
    ctx.beginPath();
    ctx.rect(-gardenWidth / 2, -gardenHeight / 2, gardenWidth, gardenHeight);
    
    // If rooms exist, subtract them from the garden
    if (rooms && rooms.length > 0) {
      for (const room of rooms) {
        if (room.walls && room.walls.length >= 3) {
          ctx.moveTo(room.walls[0].start.x - center.x, room.walls[0].start.y - center.y);
          for (const wall of room.walls) {
            ctx.lineTo(wall.end.x - center.x, wall.end.y - center.y);
          }
          ctx.closePath();
        }
      }
    }
    
    // Apply clipping region with even-odd rule
    ctx.clip('evenodd');
    
    // Fill with light green background within the clip
    // 🏢 ESTATE MODE: Use light gray for B&W rendering
    ctx.fillStyle = isEstate ? 'rgba(0, 0, 0, 0.08)' : 'rgba(34, 197, 94, 0.15)';
    ctx.fillRect(-gardenWidth / 2 - 10, -gardenHeight / 2 - 10, gardenWidth + 20, gardenHeight + 20);
    
    // Restore to remove clip before drawing border
    ctx.restore();
    
    // Save again for border drawing
    ctx.save();
    
    // Draw dotted border rectangle
    ctx.beginPath();
    ctx.rect(-gardenWidth / 2, -gardenHeight / 2, gardenWidth, gardenHeight);
    // 🏢 ESTATE MODE: Use black for B&W rendering
    ctx.strokeStyle = isEstate ? '#000000' : '#22c55e';
    ctx.lineWidth = 2 / scale;
    ctx.setLineDash([10 / scale, 5 / scale]);
    ctx.stroke();
    ctx.setLineDash([]);
    
    // CRITICAL: Reset canvas state before restore to prevent leaking
    ctx.fillStyle = 'transparent';
    ctx.strokeStyle = '#000000';
    ctx.globalAlpha = 1.0;
    
    ctx.restore();
  }
  
  // Use labelOffset for movable label, default to center
  const labelOffsetX = element.labelOffset?.x || 0;
  const labelOffsetY = element.labelOffset?.y || 0;
  
  // Use custom label or default "GARDEN" text
  const labelText = element.customLabel || 'GARDEN';
  
  // Draw label text at offset position
  // 🏢 ESTATE MODE: Use black for B&W rendering
  ctx.fillStyle = isEstate ? '#000000' : '#059669';
  ctx.font = `bold ${Math.max(12, size * 1.5)}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(labelText, labelOffsetX, labelOffsetY);
  
  // Draw area label below main text - use custom or calculated
  const areaText = element.customArea || (element.area ? `${element.area.toFixed(2)} m²` : '');
  if (areaText) {
    // 🏢 ESTATE MODE: Use black for B&W rendering
    ctx.fillStyle = isEstate ? '#000000' : '#047857';
    ctx.font = `${Math.max(10, size * 1.0)}px Arial`;
    ctx.fillText(areaText, labelOffsetX, labelOffsetY + size * 2);
  }
  
  // Draw corner resize handles if garden tool is active (only for rectangular gardens)
  if (showHandles && !element.points && element.width && element.height) {
    const gardenWidth = element.width;
    const gardenHeight = element.height;
    const handleSize = 8 / scale;
    const corners = [
      { x: -gardenWidth / 2, y: -gardenHeight / 2 }, // top-left
      { x: gardenWidth / 2, y: -gardenHeight / 2 },  // top-right
      { x: -gardenWidth / 2, y: gardenHeight / 2 },  // bottom-left
      { x: gardenWidth / 2, y: gardenHeight / 2 }    // bottom-right
    ];
    
    corners.forEach(corner => {
      ctx.fillStyle = '#22c55e';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2 / scale;
      ctx.fillRect(corner.x - handleSize / 2, corner.y - handleSize / 2, handleSize, handleSize);
      ctx.setLineDash([]);
      ctx.strokeRect(corner.x - handleSize / 2, corner.y - handleSize / 2, handleSize, handleSize);
    });
  }
  
  // CRITICAL: Final cleanup - reset all canvas properties before restore
  ctx.fillStyle = '#000000';
  ctx.strokeStyle = '#000000';
  ctx.globalAlpha = 1.0;
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  ctx.beginPath(); // Clear any active path
  
  ctx.restore(); // Restore initial save to prevent canvas state leaking
}

/**
 * Check if a point is on a garden label (for dragging with select tool)
 */
export function isPointOnGardenLabel(
  point: Point,
  garden: UtilityElement,
  threshold: number = 30
): boolean {
  if (garden.utilityType !== 'garden') return false;
  
  const labelOffsetX = garden.labelOffset?.x || 0;
  const labelOffsetY = garden.labelOffset?.y || 0;
  const labelX = garden.position.x + labelOffsetX;
  const labelY = garden.position.y + labelOffsetY;
  
  // Check if point is within threshold of label center
  const dist = Math.hypot(point.x - labelX, point.y - labelY);
  return dist <= threshold;
}

export function drawUtilityPreview(
    ctx: CanvasRenderingContext2D,
    preview: UtilityElement | null,
    scale: number,
    viewOffset: Point
) {
    if (!preview) return;
    
    const config = UTILITY_CONFIG[preview.utilityType];
    if (!config) return;

    ctx.save();
    // Canvas context is already transformed, no need to apply transformation again
    
    ctx.globalAlpha = 0.6;

    // 🔧 CANVAS SIZE: 50% smaller for specific utilities in canvas preview
    const needsSmallerCanvas = ['gas-meter', 'electric-meter', 'stop-tap', 'tank-cylinder', 'thermostat', 'consumer-unit', 'air-source-hp', 'fan', 'background-vent'].includes(preview.utilityType);
    const baseSize = 0.27 * PIXELS_PER_METER; // Match actual size (not 0.2)
    const canvasSizeMultiplier = needsSmallerCanvas ? 0.5 : 1.0;
    const size = (baseSize * canvasSizeMultiplier) / scale;
    
    // Handle custom drawn elements with rotation
    if (config.isDrawn) {
      ctx.save();
      ctx.translate(preview.position.x, preview.position.y);
      ctx.rotate(preview.rotation || 0);
      
      if (preview.utilityType === 'single-sink' || preview.utilityType === 'double-sink') {
        drawSink(ctx, { x: 0, y: 0 }, size, config.bgColor || '#93c5fd', preview.utilityType === 'double-sink');
      } else if (preview.utilityType === 'hob') {
        drawHob(ctx, { x: 0, y: 0 }, size, config.bgColor || '#ffffff', false);
      } else if (preview.utilityType === 'bathtub') {
        drawBathtub(ctx, { x: 0, y: 0 }, size, config.bgColor || '#ffffff', false);
      } else if (preview.utilityType === 'shower') {
        drawShower(ctx, { x: 0, y: 0 }, size, config.bgColor || '#ffffff', false);
      } else if (preview.utilityType === 'toilet') {
        drawToilet(ctx, { x: 0, y: 0 }, size, config.bgColor || '#ffffff', false);
      } else if (preview.utilityType === 'garden') {
        drawGarden(ctx, preview, scale, false, []); // No handles, no room cutout in preview
      }
      
      ctx.restore();
    } else {
      // Regular text-based utilities
      const needsRounded = preview.utilityType === 'tank-cylinder' || preview.utilityType === 'thermostat';
      drawUtilityText(ctx, config.text, preview.position, size, config.bgColor || '#f3f4f6', needsRounded);
    }

    ctx.restore();
} 